import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckRounded, PersonAddAlt1Rounded, PlayArrowRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { dataAuditApi, type AuditProgramEnrollmentsResult } from '../../lib/api/data-audit.api';
import { enrollmentApi } from '../../lib/api/enrollment.api';
import { errorText } from '../../lib/error-text';

interface ProgramRow {
  id: string;
  name: string;
}

export function DataAuditPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [programs, setPrograms] = useState<ProgramRow[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [includeNoise, setIncludeNoise] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditProgramEnrollmentsResult | null>(null);
  // Inline fix: create a missing enrollment. Keyed by `${personId}:${sectionId}`.
  const [fixing, setFixing] = useState<Set<string>>(new Set());
  const [fixed, setFixed] = useState<Set<string>>(new Set());

  const handleEnroll = async (personId: string, sectionId: string) => {
    if (!currentTenantId) return;
    const key = `${personId}:${sectionId}`;
    setFixing((prev) => new Set(prev).add(key));
    setError(null);
    try {
      await enrollmentApi.enrollStudent({
        tenantId: currentTenantId,
        studentId: personId,
        sectionId,
      });
      setFixed((prev) => new Set(prev).add(key));
    } catch (e) {
      setError(errorText(e, t('dataAudit.actions.enrollError')));
    } finally {
      setFixing((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      const rows: ProgramRow[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as ProgramRow & DocumentData)
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setPrograms(rows);
    });
    return () => unsub();
  }, [currentTenantId]);

  const handleRun = async () => {
    if (!currentTenantId || !selectedProgramId) return;
    setRunning(true);
    setError(null);
    try {
      const r = await dataAuditApi.runProgramAudit({
        tenantId: currentTenantId,
        programId: selectedProgramId,
        includeNoise,
      });
      setResult(r);
    } catch (e) {
      setError(errorText(e, t('dataAudit.error.generic')));
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const totalMissingStudents = useMemo(
    () => (result ? result.rows.filter((r) => r.missing.length > 0).length : 0),
    [result],
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack mb={2}>
        <Typography variant="h4" fontWeight={700}>
          {t('dataAudit.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dataAudit.subtitle')}
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 320, flex: 1 }}>
            <InputLabel>{t('dataAudit.program.label')}</InputLabel>
            <Select
              value={selectedProgramId}
              label={t('dataAudit.program.label')}
              onChange={(e) => setSelectedProgramId(e.target.value)}
            >
              {programs.length === 0 && (
                <MenuItem value="" disabled>
                  {t('dataAudit.program.placeholder')}
                </MenuItem>
              )}
              {programs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeNoise}
                onChange={(e) => setIncludeNoise(e.target.checked)}
              />
            }
            label={t('dataAudit.includeNoise.label')}
          />
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} /> : <PlayArrowRounded />}
            disabled={!selectedProgramId || running}
            onClick={handleRun}
          >
            {running ? t('dataAudit.running') : t('dataAudit.run')}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Stack spacing={3}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary">
              {result.program.name}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {t('dataAudit.summary.missing', {
                count: totalMissingStudents,
                total: result.rows.length,
              })}
            </Typography>
            <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
              {result.droppedNoiseSections > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('dataAudit.summary.noisySections', {
                    count: result.droppedNoiseSections,
                  })}
                />
              )}
              {result.skippedGhosts > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('dataAudit.summary.skippedGhosts', { count: result.skippedGhosts })}
                />
              )}
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('dataAudit.sections.title')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dataAudit.sections.code')}</TableCell>
                    <TableCell>{t('dataAudit.sections.name')}</TableCell>
                    <TableCell>{t('dataAudit.sections.startDate')}</TableCell>
                    <TableCell>{t('dataAudit.sections.term')}</TableCell>
                    <TableCell align="right">{t('dataAudit.sections.missingCount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.sections.map((s) => {
                    const missing = result.perSectionMissing.find((p) => p.sectionId === s.id);
                    return (
                      <TableRow key={s.id}>
                        <TableCell>{s.courseCode || '—'}</TableCell>
                        <TableCell>{s.courseName}</TableCell>
                        <TableCell>{s.startDate ?? '—'}</TableCell>
                        <TableCell>{s.termName ?? '—'}</TableCell>
                        <TableCell align="right">
                          {missing && missing.missingCount > 0 ? (
                            <Chip
                              size="small"
                              color="warning"
                              label={`${missing.missingCount}/${result.rows.length}`}
                            />
                          ) : (
                            <Chip size="small" color="success" variant="outlined" label="0" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('dataAudit.rows.title')}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dataAudit.rows.name')}</TableCell>
                    <TableCell>{t('dataAudit.rows.email')}</TableCell>
                    <TableCell align="right">{t('dataAudit.rows.enrolledCount')}</TableCell>
                    <TableCell>{t('dataAudit.rows.missingSections')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((r) => (
                    <TableRow key={r.personId}>
                      <TableCell>{r.fullName}</TableCell>
                      <TableCell>{r.email ?? '—'}</TableCell>
                      <TableCell align="right">
                        {r.enrolledCount}/{r.totalActive}
                      </TableCell>
                      <TableCell>
                        {r.missing.length === 0 ? (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={t('dataAudit.rows.allOk')}
                          />
                        ) : (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {r.missing.map((m) => {
                              const key = `${r.personId}:${m.sectionId}`;
                              if (fixed.has(key)) {
                                return (
                                  <Chip
                                    key={m.sectionId}
                                    size="small"
                                    color="success"
                                    icon={<CheckRounded sx={{ fontSize: 14 }} />}
                                    label={`${m.courseCode || m.courseName} · ${t('dataAudit.actions.enrolled')}`}
                                  />
                                );
                              }
                              return (
                                <Chip
                                  key={m.sectionId}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={m.courseCode || m.courseName}
                                  onDelete={() => handleEnroll(r.personId, m.sectionId)}
                                  disabled={fixing.has(key)}
                                  deleteIcon={
                                    fixing.has(key) ? (
                                      <CircularProgress size={13} />
                                    ) : (
                                      <Tooltip title={t('dataAudit.actions.enroll')}>
                                        <PersonAddAlt1Rounded sx={{ fontSize: 15 }} />
                                      </Tooltip>
                                    )
                                  }
                                />
                              );
                            })}
                            {r.droppedOrInvalid.map((d) => (
                              <Chip
                                key={d.sectionId}
                                size="small"
                                color="default"
                                variant="outlined"
                                label={`${d.courseCode || d.sectionId}=${d.status}`}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
